import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { trpc } from "@/Utils/trpc";
import { timezoneStore, useConfig } from "@/api/hooks/useConfig";
import { BarChart } from "@/components/BarChart";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import {
  getPeriodicity,
  getTimeFrame,
} from "@/components/pageCards/filterSort/filterTimeFrame";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { useTrans } from "@/translations";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { capitalize } from "lodash";
import moment from "moment-timezone";
import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";
import { TerminalAggregatesPeriod } from "server/dwhApi/terminals/types.js";

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const RecyclingRateOverTimeMWM: React.FC<Props> = ({
  timeFilter,
  setTimeFilters,
}) => {
  const { language } = useTrans();
  const { config } = useConfig();
  const { terminalConfig } = useConfigService();
  const { client } = useParams();
  const { setRecyclingRateLoading } = useHomeLoadingState();
  const { timezone } = timezoneStore();
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as ActiveOptions<any>);

  const defaultPeriod = useMemo(
    () =>
      timeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last6FullMonths,
    [terminalConfig, timeFilter],
  );

  const filters: FilterSort[] = useMemo(
    () => [
      createTimeFilters({ defaultValue: defaultPeriod }, [
        FilterTimeFrameValue.Last6FullMonths,
        FilterTimeFrameValue.LastFullYear,
      ]),
    ],
    [defaultPeriod],
  );

  const periodFilterValue = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const {
    data: aggregatePeriodData,
    isError: isAggregatePeriodError,
    isLoading: isAggregatePeriodLoading,
  } = trpc.dwh.terminals.getAggregatesPeriods.useQuery(
    {
      periodicity: getPeriodicity(periodFilterValue.startTimeISO),
      timeZone: timezone,
    },
    {
      queryKeyHashFn: () =>
        [
          "terminalAggregatePeriods",
          periodFilterValue.startTimeISO,
          timezone,
        ].toString(),
    },
  );

  const currentMonthTimeValue = useMemo(
    () => ({
      startTimeISO: moment().tz(timezone, true).startOf("month").toISOString(),
      endTimeISO: moment().tz(timezone, true).endOf("day").toISOString(),
    }),
    [timezone],
  );

  const {
    data: currentMonthData,
    isLoading: isCurrentMonthLoading,
    isError: isCurrentMonthError,
  } = trpc.dwh.terminals.getAggregates.useQuery(
    {
      startTimeISO: currentMonthTimeValue.startTimeISO,
      endTimeISO: currentMonthTimeValue.endTimeISO,
    },
    {
      queryKeyHashFn: () =>
        [
          "terminalAggregates",
          currentMonthTimeValue.startTimeISO,
          currentMonthTimeValue.endTimeISO,
        ].toString(),
    },
  );

  useEffect(() => {
    setRecyclingRateLoading(isAggregatePeriodLoading || isCurrentMonthLoading);
  }, [
    setRecyclingRateLoading,
    isAggregatePeriodLoading,
    isCurrentMonthLoading,
  ]);

  const getPeriodValue = useCallback(
    (startTime: string) =>
      capitalize(moment(startTime).tz(timezone).locale(language).format("MMM")),
    [language, timezone],
  );

  const recyData = useMemo(() => {
    const olderMonthsData =
      aggregatePeriodData?.periods.map((p: TerminalAggregatesPeriod) => {
        const {
          terminalAggregates: { occurrenceBasedSortingDegree },
        } = p;
        const period = getPeriodValue(p.startTime);

        return {
          name: period,
          value: occurrenceBasedSortingDegree * 100,
        };
      }) ?? [];

    const data = olderMonthsData;
    if (currentMonthData) {
      const { occurrenceBasedSortingDegree, totalOccurrences } =
        currentMonthData.terminalAggregates;

      if (totalOccurrences > 0) {
        const maxNrOfMonths =
          selectedPeriod?.value === FilterTimeFrameValue.LastFullYear ? 12 : 6;
        if (data.length === maxNrOfMonths) {
          data.shift();
        }

        data.push({
          name: capitalize(moment().tz(timezone, true).format("MMM")),
          value: occurrenceBasedSortingDegree * 100,
        });
      }
    }

    return data;
  }, [
    aggregatePeriodData,
    currentMonthData,
    getPeriodValue,
    selectedPeriod,
    timezone,
  ]);

  const colors = useMemo(
    () => ({
      value: terminalConfig?.primaryColor || config?.primaryColor || "#72723B",
    }),
    [config, terminalConfig],
  );

  const customerName = capitalize(client);

  const chart = useMemo(() => {
    if (isAggregatePeriodLoading || isCurrentMonthLoading) {
      return (
        <div className="flex h-full min-h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    if (isAggregatePeriodError || isCurrentMonthError) {
      return <EmptyView type="failToFetch" className="min-h-[300px]" />;
    }

    if (!aggregatePeriodData?.metadata.eventsExist) {
      return <EmptyView type="noData" className="min-h-[300px]" />;
    }

    const isEmpty = !recyData.find((d) => d.value > 0);
    if (isEmpty) {
      return (
        <EmptyView
          type="noDataCalculationPeriodUncomplete"
          className="min-h-[300px]"
        />
      );
    }

    return (
      <BarChart
        legendName={customerName}
        data={recyData}
        unit=" %"
        showAverage={false}
        showLegend={false}
        enableGridY={true}
        axisLeft={{
          format: (val: string | number) => {
            const value = typeof val === "string" ? parseFloat(val) : val;
            return value + " %";
          },
        }}
        margin={{ top: 5, right: 0, bottom: 25, left: 40 }}
        maxValue={100}
        colors={colors}
      />
    );
  }, [
    recyData,
    isAggregatePeriodError,
    isCurrentMonthError,
    isAggregatePeriodLoading,
    isCurrentMonthLoading,
    customerName,
    colors,
    aggregatePeriodData,
  ]);

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.RecyclingRateOverTime,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  return (
    <Card
      title={"circularityLabels:recyclingRateOverTime"}
      filters={filters}
      onFilterOptionChange={onFilterOptionChange}
      filterLocation={CardComponentFilterLocation.RIGHT}
    >
      {chart}
    </Card>
  );
};
